import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowDownUp,
  ChefHat,
  ChevronDown,
  CheckCircle2,
  Coins,
  Droplets,
  ExternalLink,
  Home,
  Info,
  Loader2,
  Settings2,
  Sparkles,
  Sprout,
  Sun,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import {
  useWallet,
  quoteSwap,
  synergyBonus as computeSynergyBonus,
  USD_PER_TOKEN,
  type TokenSym,
} from "@/hooks/use-wallet";
import {
  SOLAR_TAX_ROUTING,
  SOLAR_TAX_TOTAL_PCT,
  taxShare,
} from "@/lib/ecosystem-constants";

/* ────────────────────────────────────────────────────────────────── */
/* Ecosystem token registry — mock AMM reference prices in USD       */
/* ────────────────────────────────────────────────────────────────── */

type TokenKey = Exclude<TokenSym, "USDC" | "SOL" | "MEAL3" | "MEAL5">;

type Token = {
  key: TokenKey;
  symbol: string;
  name: string;
  icon: typeof Sun;
  accent: string;
  usd: number;
  liquidity: number; // pool depth in USD
  utilization: number; // 0-1
};

const TOKENS: Token[] = [
  {
    key: "MEAL2",
    symbol: "MEAL2",
    name: "Daily Utility Credit",
    icon: ChefHat,
    accent: "oklch(0.96 0.07 155)",
    usd: USD_PER_TOKEN["MEAL2"],
    liquidity: 482_000,
    utilization: 0.62,
  },
  {
    key: "MEAL4",
    symbol: "MEAL4",
    name: "Meal Anchor",
    icon: Coins,
    accent: "oklch(0.97 0.13 120)",
    usd: USD_PER_TOKEN["MEAL4"],
    liquidity: 318_000,
    utilization: 0.48,
  },
  {
    key: "SOLAR-PANDO",
    symbol: "SOLAR",
    name: "SolarPando Yield",
    icon: Sun,
    accent: "oklch(0.86 0.16 90)",
    usd: USD_PER_TOKEN["SOLAR-PANDO"],
    liquidity: 214_000,
    utilization: 0.71,
  },
  {
    key: "AQUA-PANDO",
    symbol: "AQUA",
    name: "AquaPando Reserves",
    icon: Droplets,
    accent: "oklch(0.8 0.14 230)",
    usd: USD_PER_TOKEN["AQUA-PANDO"],
    liquidity: 168_400,
    utilization: 0.54,
  },
  {
    key: "PLANTY-PANDO",
    symbol: "PLANTY",
    name: "PlantyPando Land",
    icon: Sprout,
    accent: "oklch(0.84 0.16 160)",
    usd: USD_PER_TOKEN["PLANTY-PANDO"],
    liquidity: 296_000,
    utilization: 0.39,
  },
  {
    key: "VILLAGE-PANDO",
    symbol: "VILLAGE",
    name: "VillagePando Governance",
    icon: Home,
    accent: "oklch(0.92 0.05 90)",
    usd: USD_PER_TOKEN["VILLAGE-PANDO"],
    liquidity: 604_000,
    utilization: 0.57,
  },
];

const TOKEN_MAP: Record<TokenKey, Token> = TOKENS.reduce(
  (acc, t) => ({ ...acc, [t.key]: t }),
  {} as Record<TokenKey, Token>,
);

const SLIPPAGE = 0.005; // 0.5%
const NETWORK_FEE_USD = 0.008;

function formatNum(n: number, max = 6) {
  if (!isFinite(n) || n === 0) return "0";
  if (n >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return n.toLocaleString("en-US", { maximumFractionDigits: max });
}
function formatUsd(n: number) {
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/* ────────────────────────────────────────────────────────────────── */

type TxState = "idle" | "routing" | "success";

export function EcosystemSwap() {
  const { t } = useI18n();
  const wallet = useWallet();
  const { connected, balances, openConnect, swap: runSwap } = wallet;
  const [fromKey, setFromKey] = useState<TokenKey>("SOLAR-PANDO");
  const [toKey, setToKey] = useState<TokenKey>("MEAL4");
  const [amount, setAmount] = useState<string>("100");
  const [tx, setTx] = useState<TxState>("idle");
  const [txHash, setTxHash] = useState<string>("");
  const [txOut, setTxOut] = useState<number>(0);
  const [pickerOpen, setPickerOpen] = useState<null | "from" | "to">(null);

  const tokenKeyToI18n: Record<TokenKey, string> = {
    "MEAL2": "meal2", "MEAL4": "meal4",
    "SOLAR-PANDO": "solar", "AQUA-PANDO": "aqua",
    "PLANTY-PANDO": "planty", "VILLAGE-PANDO": "village",
  };
  const localizeToken = (tok: Token): Token => ({ ...tok, name: t(`vp.swap.token.${tokenKeyToI18n[tok.key]}.name`) });
  const from = localizeToken(TOKEN_MAP[fromKey]);
  const to = localizeToken(TOKEN_MAP[toKey]);

  const parsed = Math.max(0, Number(amount) || 0);
  const fromBalance = balances[fromKey] ?? 0;
  const toBalance = balances[toKey] ?? 0;

  const synergyBonus = useMemo(
    () => computeSynergyBonus(fromKey, toKey),
    [fromKey, toKey],
  );

  const rawOut = useMemo(
    () => quoteSwap(fromKey, toKey, parsed).out,
    [fromKey, toKey, parsed],
  );

  const rate = parsed > 0 ? rawOut / parsed : 0;
  const priceImpact = useMemo(() => {
    const usdIn = parsed * from.usd;
    return Math.min(0.03, (usdIn / (from.liquidity + to.liquidity)) * 0.5);
  }, [parsed, from]);

  function flip() {
    setFromKey(toKey);
    setToKey(fromKey);
  }

  async function handleSwap() {
    if (!connected) {
      openConnect();
      return;
    }
    if (parsed <= 0 || fromKey === toKey) return;
    if (parsed > fromBalance) return;
    setTx("routing");
    try {
      const res = await runSwap(fromKey, toKey, parsed);
      setTxHash(res.hash);
      setTxOut(res.out);
      setTx("success");
    } catch {
      setTx("idle");
    }
  }

  return (
    <section id="swap" className="mt-16">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
            {t("vp.swap.eyebrow")}
          </span>
          <h2 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
            {t("vp.swap.title")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {t("vp.swap.sub")}
          </p>
        </div>
        <ConnectButton />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.15fr_1fr]">
        {/* Swap card */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.12_0.012_260_/_0.6)] p-5 backdrop-blur-xl sm:p-6">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full blur-3xl"
            style={{ background: `${from.accent.replace(")", " / 0.18)")}` }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full blur-3xl"
            style={{ background: `${to.accent.replace(")", " / 0.14)")}` }}
          />

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/70">
              <Sparkles className="h-3.5 w-3.5 text-[oklch(0.97_0.13_120)]" />
              {t("vp.swap.smartRouterLabel")}
            </div>
            <button
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
              aria-label={t("vp.swap.settingsAria")}
            >
              <Settings2 className="h-3 w-3" />
              {t("vp.swap.slippageButton")}
            </button>
          </div>

          {/* From */}
          <SwapPanel
            label={t("vp.swap.panel.from")}
            token={from}
            balance={fromBalance}
            amount={amount}
            onAmount={setAmount}
            onPick={() => setPickerOpen("from")}
            usd={parsed * from.usd}
            connected={connected}
          />

          {/* Flip */}
          <div className="relative z-10 my-2 flex justify-center">
            <button
              onClick={flip}
              className="grid h-9 w-9 place-items-center rounded-xl border border-white/15 bg-[oklch(0.14_0.012_260)] text-foreground/80 transition-all hover:rotate-180 hover:border-[oklch(0.97_0.13_120)]/60 hover:text-[oklch(0.97_0.13_120)]"
              aria-label={t("vp.swap.flipAria")}
            >
              <ArrowDownUp className="h-4 w-4" />
            </button>
          </div>

          {/* To */}
          <SwapPanel
            label={t("vp.swap.panel.to")}
            token={to}
            balance={toBalance}
            amount={rawOut > 0 ? formatNum(rawOut, 4) : ""}
            onPick={() => setPickerOpen("to")}
            usd={rawOut * to.usd}
            readOnly
            connected={connected}
          />


          {/* Details */}
          <div className="mt-5 space-y-2 rounded-2xl border border-white/5 bg-black/25 p-4 font-mono text-[11px]">
            <DetailRow
              label={t("vp.swap.detail.rate.label")}
              value={
                fromKey === toKey
                  ? t("vp.swap.detail.rate.same")
                  : `1 ${from.symbol} ≈ ${formatNum(rate, 4)} ${to.symbol}`
              }
            />
            <DetailRow
              label={t("vp.swap.detail.slippage.label")}
              value={t("vp.swap.detail.slippage.value")}
              hint={t("vp.swap.detail.slippage.hint")}
            />
            <DetailRow
              label={t("vp.swap.detail.priceImpact.label")}
              value={`${(priceImpact * 100).toFixed(2)}%`}
              tone={priceImpact > 0.015 ? "warn" : "ok"}
            />
            <DetailRow
              label={t("vp.swap.detail.networkFee.label")}
              value={`${formatUsd(NETWORK_FEE_USD)} ${t("vp.swap.detail.networkFee.suffix")}`}
              hint={t("vp.swap.detail.networkFee.hint")}
            />
            <DetailRow
              label={t("vp.swap.detail.synergyBonus.label")}
              value={`+${(synergyBonus * 100).toFixed(2)}%`}
              tone={synergyBonus > 0 ? "bonus" : "ok"}
              highlight={fromKey === "SOLAR-PANDO"}
            />
            <div className="!mt-3 flex items-center justify-between border-t border-white/5 pt-3 text-foreground">
              <span className="text-muted-foreground">{t("vp.swap.detail.minReceived.label")}</span>
              <span>
                {formatNum(rawOut * (1 - SLIPPAGE), 4)} {to.symbol}
              </span>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleSwap}
            disabled={tx === "routing" || (connected && (parsed <= 0 || fromKey === toKey))}
            className={cn(
              "relative mt-5 flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-5 py-4 font-mono text-xs uppercase tracking-[0.22em] transition-all",
              "bg-gradient-to-r from-[oklch(0.97_0.13_120)] to-[oklch(0.86_0.16_90)] text-black",
              "hover:shadow-[0_0_40px_oklch(0.97_0.13_120_/_0.45)] disabled:opacity-50 disabled:hover:shadow-none",
            )}
          >
            {tx === "routing" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("vp.swap.cta.routing")}
              </>
            ) : !connected ? (
              <>
                <Wallet className="h-4 w-4" />
                {t("vp.swap.cta.connectWallet")}
              </>
            ) : fromKey === toKey ? (
              t("vp.swap.cta.selectDifferent")
            ) : parsed <= 0 ? (
              t("vp.swap.cta.enterAmount")
            ) : (
              <>
                <Zap className="h-4 w-4" />
                {t("vp.swap.cta.swap")}
              </>
            )}
          </button>
        </div>

        {/* Liquidity pool stats */}
        <LiquidityPanel from={from} to={to} />
      </div>

      {/* SolarPando 5% transaction tax — public disclosure */}
      <SolarTaxDisclosure />


      {/* Token picker */}
      <AnimatePresence>
        {pickerOpen && (
          <TokenPicker
            side={pickerOpen}
            current={pickerOpen === "from" ? fromKey : toKey}
            onSelect={(key) => {
              if (pickerOpen === "from") setFromKey(key);
              else setToKey(key);
              setPickerOpen(null);
            }}
            onClose={() => setPickerOpen(null)}
          />
        )}
      </AnimatePresence>

      {/* Connect modal is provided globally by <WalletProvider>. */}


      {/* Success modal */}
      <AnimatePresence>
        {tx === "success" && (
          <SuccessModal
            hash={txHash}
            from={from}
            to={to}
            amountIn={parsed}
            amountOut={txOut || rawOut}
            bonus={synergyBonus}
            onClose={() => {
              setTx("idle");
              setTxHash("");
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function ConnectButton() {
  const { t } = useI18n();
  const { connected, address, openConnect, disconnect } = useWallet();
  const short = address
    ? address.includes("…")
      ? address
      : `${address.slice(0, 4)}…${address.slice(-4)}`
    : "";
  return (
    <button
      onClick={connected ? disconnect : openConnect}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all",
        connected
          ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/15"
          : "border-white/15 bg-white/[0.04] text-foreground hover:bg-white/[0.08]",
      )}
    >
      {connected ? (
        <>
          <CheckCircle2 className="h-3.5 w-3.5" />
          {short}
        </>
      ) : (
        <>
          <Wallet className="h-3.5 w-3.5" />
          {t("vp.rwa.connect.cta")}
        </>
      )}
    </button>
  );
}

function SwapPanel({
  label,
  token,
  balance,
  amount,
  onAmount,
  onPick,
  usd,
  readOnly,
  connected,
}: {
  label: string;
  token: Token;
  balance: number;
  amount?: string;
  onAmount?: (v: string) => void;
  onPick: () => void;
  usd: number;
  readOnly?: boolean;
  connected: boolean;
}) {
  const { t } = useI18n();
  const Icon = token.icon;
  return (
    <div className="relative mt-5 rounded-2xl border border-white/10 bg-black/25 p-4">
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        <span>{label}</span>
        {connected && (
          <button
            className="hover:text-foreground"
            onClick={() => onAmount?.(String(balance))}
            disabled={readOnly}
          >
            {t("vp.swap.panel.balancePrefix")} {formatNum(balance, 2)} {token.symbol}
          </button>
        )}
      </div>

      <div className="mt-3 flex items-center gap-3">
        <input
          inputMode="decimal"
          value={amount ?? ""}
          onChange={(e) => onAmount?.(e.target.value.replace(/[^0-9.]/g, ""))}
          readOnly={readOnly}
          placeholder={t("vp.swap.panel.amountPlaceholder")}
          className="min-w-0 flex-1 bg-transparent font-display text-3xl font-semibold tracking-tight text-foreground outline-none placeholder:text-foreground/25"
        />
        <button
          onClick={onPick}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 transition-colors hover:bg-white/[0.08]"
          style={{ boxShadow: `0 0 22px ${token.accent.replace(")", " / 0.20)")}` }}
        >
          <span
            className="grid h-6 w-6 place-items-center rounded-full border border-white/15 bg-white/[0.05]"
          >
            <Icon className="h-3.5 w-3.5" style={{ color: token.accent }} />
          </span>
          <span className="font-display text-sm font-semibold">{token.symbol}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          ≈ {formatUsd(usd)}
        </span>
        {token.key === "VILLAGE-PANDO" && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 bg-amber-300/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-amber-200"
            title={t("vp.swap.pegged.tooltip")}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-amber-300 shadow-[0_0_8px_rgb(252_211_77_/_0.9)]" />
            {t("vp.swap.pegged.badge")}
          </span>
        )}
      </div>
    </div>
  );
}


function DetailRow({
  label,
  value,
  hint,
  tone,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "ok" | "warn" | "bonus";
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        {label}
        {hint && (
          <span className="group relative">
            <Info className="h-3 w-3 text-foreground/40" />
            <span className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 hidden w-52 -translate-x-1/2 rounded-md border border-white/10 bg-black/90 p-2 text-[10px] normal-case tracking-normal text-foreground/80 group-hover:block">
              {hint}
            </span>
          </span>
        )}
      </span>
      <span
        className={cn(
          "text-foreground",
          tone === "warn" && "text-amber-300",
          tone === "bonus" && "text-emerald-300",
          highlight && "drop-shadow-[0_0_6px_oklch(0.78_0.18_150_/_0.6)]",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function LiquidityPanel({ from, to }: { from: Token; to: Token }) {
  const { t } = useI18n();
  const pair = `${from.symbol} / ${to.symbol}`;
  const depth = (from.liquidity + to.liquidity) / 2;
  const totalTvl = TOKENS.reduce((s, t) => s + t.liquidity, 0);
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
            {t("vp.swap.liquidity.eyebrow")}
          </div>
          <div className="mt-1 font-display text-lg font-semibold">{pair}</div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-emerald-300">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          {t("vp.swap.liquidity.stableBadge")}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <StatBox label={t("vp.swap.liquidity.stat.pairDepth")} value={formatUsd(depth)} />
        <StatBox label={t("vp.swap.liquidity.stat.volume24h")} value={formatUsd(depth * 0.18)} />
        <StatBox label={t("vp.swap.liquidity.stat.feeTier")} value={t("vp.swap.liquidity.stat.feeTierValue")} />
        <StatBox label={t("vp.swap.liquidity.stat.ecosystemTvl")} value={formatUsd(totalTvl)} />
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>{t("vp.swap.liquidity.utilizationLabel")}</span>
          <span>{TOKENS.length} {t("vp.swap.liquidity.poolsSuffix")}</span>
        </div>
        <div className="space-y-2.5">
          {TOKENS.map((tok) => (
            <div key={tok.key} className="flex items-center gap-3">
              <span className="w-16 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                {tok.symbol}
              </span>
              <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.05]">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${tok.utilization * 100}%` }}
                  transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    background: `linear-gradient(90deg, ${tok.accent}, ${tok.accent.replace(")", " / 0.4)")})`,
                    boxShadow: `0 0 14px ${tok.accent.replace(")", " / 0.5)")}`,
                  }}
                />
              </div>
              <span className="w-10 text-right font-mono text-[10px] text-foreground/80">
                {Math.round(tok.utilization * 100)}%
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-white/5 bg-black/25 p-3 text-[11px] leading-relaxed text-muted-foreground">
        {t("vp.swap.liquidity.disclaimer")}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Where does the 5% go?  — SolarPando transaction tax disclosure    */
/* ────────────────────────────────────────────────────────────────── */

function SolarTaxDisclosure() {
  const { t } = useI18n();
  const labelFor = (k: string) =>
    k === "hardware"
      ? t("vp.swap.tax.hardware.label")
      : k === "reforestation"
        ? t("vp.swap.tax.reforestation.label")
        : t("vp.swap.tax.ops.label");
  const detailFor = (k: string) =>
    k === "hardware"
      ? t("vp.swap.tax.hardware.detail")
      : k === "reforestation"
        ? t("vp.swap.tax.reforestation.detail")
        : t("vp.swap.tax.ops.detail");
  return (
    <section
      aria-labelledby="solar-tax-title"
      className="mt-8 overflow-hidden rounded-3xl border border-amber-300/20 bg-gradient-to-br from-amber-500/[0.04] via-white/[0.02] to-emerald-500/[0.04] p-5 sm:p-6"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/80">
            {t("vp.swap.tax.eyebrow")}
          </div>
          <h3
            id="solar-tax-title"
            className="mt-1.5 font-display text-2xl font-medium tracking-tight text-foreground"
          >
            {t("vp.swap.tax.title")}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">{t("vp.swap.tax.intro")}</p>
        </div>
        <div className="shrink-0 rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-center">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("vp.swap.tax.total")}
          </div>
          <div className="mt-1 font-display text-3xl font-semibold tracking-tight text-amber-200">
            {SOLAR_TAX_TOTAL_PCT}%
          </div>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="mt-5 flex h-2.5 w-full overflow-hidden rounded-full border border-white/10 bg-black/40">
        {SOLAR_TAX_ROUTING.map((a) => (
          <div
            key={a.key}
            style={{
              width: `${taxShare(a) * 100}%`,
              background: a.accent,
              boxShadow: `0 0 12px ${a.accent.replace(")", " / 0.55)")}`,
            }}
            aria-label={`${labelFor(a.key)}: ${a.pct}%`}
          />
        ))}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {SOLAR_TAX_ROUTING.map((a) => (
          <div
            key={a.key}
            className="rounded-2xl border border-white/10 bg-black/30 p-4"
            style={{ boxShadow: `0 0 22px ${a.accent.replace(")", " / 0.18)")}` }}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-display text-lg font-semibold tracking-tight text-foreground">
                {labelFor(a.key)}
              </span>
              <span
                className="font-mono text-xs font-semibold"
                style={{ color: a.accent }}
              >
                {Math.round(taxShare(a) * 100)}%
              </span>
            </div>
            <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              {t("vp.swap.tax.perTx").replace("{pct}", String(a.pct))}
            </div>
            <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
              {detailFor(a.key)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/25 p-3">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-display text-base font-semibold tracking-tight">{value}</div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function TokenPicker({
  side,
  current,
  onSelect,
  onClose,
}: {
  side: "from" | "to";
  current: TokenKey;
  onSelect: (k: TokenKey) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <Overlay onClose={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
        className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.12_0.012_260_/_0.95)] p-5 backdrop-blur-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
            {side === "from" ? t("vp.swap.picker.eyebrowInput") : t("vp.swap.picker.eyebrowOutput")}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="mt-4 space-y-1.5">
          {TOKENS.map((tok) => {
            const Icon = tok.icon;
            const active = tok.key === current;
            return (
              <li key={tok.key}>
                <button
                  onClick={() => onSelect(tok.key)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                    active
                      ? "border-[oklch(0.97_0.13_120)]/50 bg-[oklch(0.97_0.13_120)]/5"
                      : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]",
                  )}
                >
                  <span
                    className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-white/[0.04]"
                    style={{ boxShadow: `0 0 18px ${tok.accent.replace(")", " / 0.25)")}` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: tok.accent }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm font-semibold">{tok.key}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{t(`vp.swap.token.${({"MEAL2":"meal2","MEAL4":"meal4","SOLAR-PANDO":"solar","AQUA-PANDO":"aqua","PLANTY-PANDO":"planty","VILLAGE-PANDO":"village"} as const)[tok.key]}.name`)}</div>
                  </div>
                  <div className="text-right font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    {formatUsd(tok.usd)}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </motion.div>
    </Overlay>
  );
}

function ConnectModal({
  onConfirm,
  onClose,
}: {
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const wallets = [
    { name: "Phantom", tag: t("vp.swap.connectModal.wallet.phantom.tag") },
    { name: "MetaMask", tag: t("vp.swap.connectModal.wallet.metamask.tag") },
    { name: "WalletConnect", tag: t("vp.swap.connectModal.wallet.walletconnect.tag") },
  ];
  return (
    <Overlay onClose={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[oklch(0.12_0.012_260_/_0.95)] p-6 backdrop-blur-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="connect-title"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 right-0 h-48 w-48 rounded-full blur-3xl"
          style={{ background: "oklch(0.97 0.13 120 / 0.18)" }}
        />
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/60">
              {t("vp.swap.connectModal.eyebrow")}
            </div>
            <h3 id="connect-title" className="mt-1 font-display text-xl font-semibold tracking-tight">
              {t("vp.swap.connectModal.title")}
            </h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("vp.swap.connectModal.body")}
        </p>
        <ul className="mt-5 space-y-2">
          {wallets.map((w) => (
            <li key={w.name}>
              <button
                onClick={onConfirm}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-[oklch(0.97_0.13_120)]/40 hover:bg-white/[0.06]"
              >
                <div>
                  <div className="font-display text-sm font-semibold">{w.name}</div>
                  <div className="text-[11px] text-muted-foreground">{w.tag}</div>
                </div>
                <ChevronDown className="h-4 w-4 -rotate-90 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
          {t("vp.swap.connectModal.disclaimer")}
        </p>
      </motion.div>
    </Overlay>
  );
}

function SuccessModal({
  hash,
  from,
  to,
  amountIn,
  amountOut,
  bonus,
  onClose,
}: {
  hash: string;
  from: Token;
  to: Token;
  amountIn: number;
  amountOut: number;
  bonus?: number;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const short = `${hash.slice(0, 10)}…${hash.slice(-6)}`;
  return (
    <Overlay onClose={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 12 }}
        transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative w-full max-w-md overflow-hidden rounded-3xl border border-emerald-400/30 bg-[oklch(0.12_0.012_260_/_0.95)] p-6 backdrop-blur-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(50% 40% at 50% 0%, oklch(0.78 0.18 150 / 0.18), transparent 70%)",
          }}
        />
        <div className="relative flex flex-col items-center text-center">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className="grid h-14 w-14 place-items-center rounded-full border border-emerald-400/40 bg-emerald-400/10"
          >
            <CheckCircle2 className="h-7 w-7 text-emerald-300" />
          </motion.div>
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300">
            {t("vp.swap.success.eyebrow")}
          </div>
          <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight">
            {t("vp.swap.success.title")}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("vp.swap.success.routedPrefix")} {formatNum(amountIn, 4)} {from.symbol} → {formatNum(amountOut, 4)} {to.symbol}
          </p>
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80 hover:bg-white/[0.06]"
          >
            {t("vp.swap.success.txHashPrefix")} {short}
            <ExternalLink className="h-3 w-3" />
          </a>
          <button
            onClick={onClose}
            className="mt-5 w-full rounded-2xl bg-gradient-to-r from-[oklch(0.97_0.13_120)] to-[oklch(0.86_0.16_90)] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] text-black transition-shadow hover:shadow-[0_0_40px_oklch(0.97_0.13_120_/_0.45)]"
          >
            {t("vp.swap.success.doneButton")}
          </button>
        </div>
      </motion.div>
    </Overlay>
  );
}

function Overlay({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      {children}
    </motion.div>
  );
}
